import React, { FC, useMemo } from 'react';
import styled from '@emotion/styled';
import { Palette, Box, Flex, Text } from '@updater/ui-uds';
import { isTaskCompletedOrValidating } from 'flows/core/utils/card-makers/utils';
import { InitiatePayload } from 'flows/core/types/cards';
import { useDaysLeft } from './utils';
import { ArrowRight } from '@phosphor-icons/react';

const Card = styled(Box)`
  background-color: ${Palette.white};
  border-radius: 1rem;
  padding: 0.75rem 1.25rem;
  min-height: 6.125rem;
  box-shadow: ${({ theme }) => theme.shadows.shadow10};
`;

const BarContainer = styled(Box)`
  width: 100%;
  height: 0.5rem;
  background-color: ${Palette.mint10};
  border-radius: 0.25rem;
  margin: 0.5rem 0;
`;

const BarFill = styled(Box)<{ widthPct: number }>`
  width: ${({ widthPct }) => widthPct}%;
  height: 100%;
  background-color: ${Palette.green};
  border-radius: 0.25rem;
`;

export interface ProgressBarCardProps {
  priorityCards: InitiatePayload[];
  completedCards: InitiatePayload[];
  currentMoveDate: string | null | undefined;
  onStartTask?: () => void;
}

export const ProgressBarCard: FC<ProgressBarCardProps> = ({
  priorityCards,
  completedCards,
  currentMoveDate,
  onStartTask,
}) => {
  const daysLeft = useDaysLeft(currentMoveDate);

  const completedRequiredTasks = useMemo(() => {
    if (!completedCards) return [];
    return completedCards.filter((card: InitiatePayload) =>
      isTaskCompletedOrValidating(card.data) && (card.data?.required || card.data?.priority)
    );
  }, [completedCards]);

  const totalTasks = (priorityCards?.length || 0) + completedRequiredTasks.length;
  const completedTasks = (priorityCards?.filter((card: InitiatePayload) => isTaskCompletedOrValidating(card.data)).length || 0) + completedRequiredTasks.length;

  const getDaysText = (): string => {
    if (daysLeft === 0) {
      return "It's move-in day!";
    } else if (daysLeft === 1) {
      return "Tomorrow's the big day!";
    } else if (daysLeft < 0) {
      return "Welcome home!";
    } else {
      return `${daysLeft} days left until your move`;
    }
  };

  const pct = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
  const remainingTasks = totalTasks - completedTasks;

  let title: string;
  if (pct === 100) title = "You're all set";
  else if (remainingTasks === 1) title = 'Just one more!';
  else if (pct === 50) title = "You're halfway there!";
  else if (pct > 50) title = "Over halfway there!";
  else if (pct === 0) title = 'Get started';
  else title = 'Moving progress';

  if (totalTasks === 0) {
    return null;
  }

  return (
    <Card data-testid="progress-bar-card">
      <Flex alignItems="center" justifyContent="space-between">
        {pct === 0 ? (
          <Flex 
            alignItems="center" 
            justifyContent="space-between"
            width="100%"
            onClick={() => onStartTask?.()}
            style={{ cursor: 'pointer' }}
          >
            <Text 
              variant="lBold" 
              style={{ color: Palette.royalBlue }}
            >
              {title}
            </Text>
            <ArrowRight size={24} color={Palette.royalBlue} />
          </Flex>
        ) : (
          <>
            <Text variant="lBold">
              {title}
            </Text>
            <Text variant="lBold">
              {pct}%
            </Text>
          </>
        )}
      </Flex>

      <BarContainer>
        <BarFill widthPct={pct} />
      </BarContainer>

      <Flex justifyContent="space-between" flexWrap="wrap" gap="0.25rem">
        {pct === 0 ? (
          <Text variant="s">
            {getDaysText()}
          </Text>
        ) : (
          <>
            <Text variant="s">
              {completedTasks} of {totalTasks} tasks done
            </Text>
            <Text variant="s">
              {getDaysText()}
            </Text>
          </>
        )}
      </Flex>
    </Card>
  );
};